import api from './index';
import axios from 'axios';

// Create a separate client for public API
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://rebase.api.seasideapis.com',
  headers: {
    'Accept': 'public api; version=1',
    'Content-Type': 'application/json'
  }
});

export const normalizePositionDetails = (position = {}) => ({
  ...position,
  company: position.company?.resource_texts
    ? { ...position.company, ...position.company.resource_texts }
    : position.company,
  job_titles: Array.isArray(position.job_titles)
    ? position.job_titles
    : [position.job_titles?.job_title].filter(Boolean),
  positions_skills: (position.positions_skills || []).map(positionSkill => (
    positionSkill.skill ? { ...positionSkill, ...positionSkill.skill } : positionSkill
  ))
});

export const APPLICATION_LOCK_MS = 24 * 60 * 60 * 1000;
export const applicationLocked = (appliedAt, now = Date.now()) =>
  now - Number(appliedAt) < APPLICATION_LOCK_MS;

const jobsAPI = {
  // Get all job titles
  getJobTitles: (config = {}) => {
    return api.get('/job_titles', {
      headers: {
        'Accept': 'shared api; version=1'
      },
      ...config
    });
  },
  
  getPositions: (jobTitleIds = [], offset = 0, perPage = 20) => {
    const params = new URLSearchParams();
    params.append('offset', offset);
    params.append('per', perPage);
    if (jobTitleIds.length > 0) {
      params.append('job_title_ids', jobTitleIds.join(','));
    }
    return api.get(`/applicants/positions?${params.toString()}`);
  },

  // Legacy context API; the Jobs page itself uses positions.
  getOffers: (jobTitleIds = null, page = 1, perPage = 20, config = {}) => {
    const params = new URLSearchParams({
      page,
      per: perPage,
      sort: config.sort || '-active_at'
    });
    if (config.includeAllJobTitles === true) {
      params.append('job_title_ids', 'none');
    } else if (jobTitleIds?.length) {
      params.append('job_title_ids', jobTitleIds.join(','));
    }
    const { sort: _sort, includeAllJobTitles: _includeAllJobTitles, ...requestConfig } = config;
    return api.get(`/applicants/offers?${params.toString()}`, requestConfig);
  },

  getOffer: (offerId) => api.get(`/applicants/offers/${offerId}?include_type=swipe`),

  applyToOffer: (offerId) => api.put(`/applicants/offers/${offerId}`, { status: 'accepted' }),
  
  // Get detailed position information from public API (like the website does)
  getPositionDetails: (positionId) => {
    return publicApi.get(`/positions/${positionId}`).then(response => ({
      ...response,
      data: normalizePositionDetails(response.data)
    }));
  },
  
  applyToPosition: (positionId) => {
    return api.post(`/applicants/positions/${positionId}/apply`);
  },

  // Saved applicant preferences
  getApplicantJobTitles: () => {
    return api.get('/applicants/job_titles');
  },

  saveApplicantJobTitle: (jobTitleId, data) => {
    return api.post('/applicants/job_titles', {
      job_title_id: jobTitleId,
      ...data
    });
  },

  updateApplicantJobTitle: (jobTitleId, data) => {
    return api.put(`/applicants/job_titles/${jobTitleId}`, data);
  },

  deleteApplicantJobTitle: (jobTitleId) => {
    return api.delete(`/applicants/job_titles/${jobTitleId}`);
  },

  getReferralLink: (resourceId = 'me') => {
    return api.get(`/referrals/${resourceId}`, {
      headers: {
        'Accept': 'shared api; version=1'
      }
    });
  }
};

export default jobsAPI;
